/**
 * Target Achievements API Endpoints
 * Centralized endpoint definitions for target achievements module
 */

import { API_BASE_URL } from '../config';

/**
 * Point Structure Endpoints
 */
export const POINT_STRUCTURE_ENDPOINTS = {
  /**
   * Get all point structure templates
   * GET loyalty-target/get-point-config-template?id=&target_basis_id=&calculation_type_id=&page=&page_size=
   * Query params: id (optional), target_basis_id (optional), calculation_type_id
   * (optional), page (optional), page_size (optional)
   * Response includes `data` (PointStructureTemplate[]) and, when page/page_size
   * are sent, `meta.pagination` (page, page_size, total_count, total_pages,
   * has_next, has_previous)
   */
  GET_TEMPLATES: `${API_BASE_URL}loyalty-target/get-point-config-template`,

  /**
   * Create point structure template
   * POST loyalty-target/create-point-config-template
   * Content-Type: multipart/form-data
   */
  CREATE_TEMPLATE: `${API_BASE_URL}loyalty-target/create-point-config-template`,
} as const;

/**
 * Brands Endpoints (used by point structure)
 */
export const BRANDS_ENDPOINTS = {
  /**
   * Get brands list
   * GET brands/get-brand?brand_category_id=&brand_name=&sku=&page=1
   * Query params: brand_category_id, brand_name, sku, page
   */
  GET_BRANDS: `${API_BASE_URL}brands/get-brand`,
} as const;

/**
 * Target Endpoints
 */
export const TARGET_ENDPOINTS = {
  /**
   * Get targets list
   * GET loyalty-target/get-target?id=&target_name=&page=&page_size=
   * Query params: id (optional), target_name (search), page, page_size
   * Response includes `data` (TargetListItem[]) and `meta.pagination`
   * (page, page_size, total_count, total_pages, has_next, has_previous)
   */
  GET_TARGETS: `${API_BASE_URL}loyalty-target/get-target`,

  /**
   * Create new target
   * POST loyalty-target/create-target
   */
  CREATE_TARGET: `${API_BASE_URL}loyalty-target/create-target`,

  /**
   * Update existing target
   * POST loyalty-target/update-target
   */
  UPDATE_TARGET: `${API_BASE_URL}loyalty-target/update-target`,

  /**
   * Assign point structure to target
   * POST loyalty-target/point-structure
   */
  ASSIGN_POINT_STRUCTURE: `${API_BASE_URL}loyalty-target/point-structure`,

  /**
   * Get target imports (assigned users)
   * GET loyalty-target/get-target-import?target_id=&status=&search=
   * Query params: target_id, status (optional), search (optional)
   */
  GET_TARGET_IMPORTS: `${API_BASE_URL}loyalty-target/get-target-import`,

  /**   * Get target import validation config (field mappings)
   * GET loyalty-target/get-target-import-validation-config?program_id=1&target_type=1&import_mode=1
   * Query params: program_id, target_type (1=month, 2=quarter, 3=cycle), import_mode (1=create, 2=update, 3=disable)
   * Response: { total_fields, fields: [{ field_name, field_code }] }
   */
  GET_IMPORT_VALIDATION_CONFIG: `${API_BASE_URL}loyalty-target/get-target-import-validation-config`,

  /**   * Validate target import (queue job for validation)
   * POST loyalty-target/validate-target-import?program_id=
   * Content-Type: multipart/form-data
   * Payload: program_id, target_id, excel_file, mapping, import_mode (1=create, 2=update, 3=disable)
   * Response: job_id, target_id, status, message
   */
  VALIDATE_TARGET_IMPORT: `${API_BASE_URL}loyalty-target/validate-target-import`,

  /**
   * Import target users via Excel
   * POST loyalty-target/target-import
   * Content-Type: multipart/form-data
   */
  IMPORT_TARGET_USERS: `${API_BASE_URL}loyalty-target/target-import-async`,

  /**
   * Update target import
   * POST loyalty-target/update-target-import
   * Content-Type: multipart/form-data
   */
  UPDATE_TARGET_IMPORT: `${API_BASE_URL}loyalty-target/update-target-import`,

  /**
   * Get full sales progress (for wizard step tracking)
   * GET queue/file-progress-status-module-wise?program_id=1&module_type=target_import
   */
  GET_FULL_SALES_PROGRESS: `${API_BASE_URL}queue/file-progress-status-module-wise`,

  /**
   * Get target gap analysis (validation results)
   * GET loyalty-target/get-target-achievement-gap-analysis?job_id=...&program_id=1
   * Query params: job_id, program_id
   * Response: summary, processing_details, upload_details, gap_details, downloads, import_status
   */
  GET_TARGET_GAP_ANALYSIS: `${API_BASE_URL}loyalty-target/get-target-achievement-gap-analysis`,

  /**
   * Import target async (submit validated data for import)
   * POST loyalty-target/target-import-async?program_id=1
   * Payload: { target_id, job_id }
   * Response: job_id, status, message, target_name, file_name, parent_job_id
   */
  TARGET_IMPORT_ASYNC: `${API_BASE_URL}loyalty-target/target-import-async`,

  /**
   * Download user target data as Excel
   * GET loyalty-target/user-target-data?program_id=1&target_id=
   */
  GET_USER_TARGET_DATA: `${API_BASE_URL}loyalty-target/user-target-data`,
} as const;

/**
 * Target Quarter Endpoints (Target-specific)
 */
export const TARGET_QUARTER_ENDPOINTS = {
  /**
   * Get quarters list
   * GET frequency/get-quarter
   */
  GET_QUARTERS: `${API_BASE_URL}frequency/get-quarter`,
} as const;

/**
 * Target Cycle Endpoints (Target-specific)
 */
export const TARGET_CYCLE_ENDPOINTS = {
  /**
   * Get cycles list
   * GET frequency/get-cycle
   */
  GET_CYCLES: `${API_BASE_URL}frequency/get-cycle`,
} as const;

/**
 * Achievements Upload & Management Endpoints
 */
export const ACHIEVEMENTS_ENDPOINTS = {
  /**
   * Get achievement import validation config (field mappings)
   * GET loyalty-target/get-achievement-import-validation-config?program_id=1
   */
  GET_IMPORT_VALIDATION_CONFIG: `${API_BASE_URL}loyalty-target/get-achievement-import-validation-config`,

  /**
   * Validate achievement import (queue job for validation)
   * POST loyalty-target/validate-achievement-import?program_id=1
   * Content-Type: multipart/form-data
   * FormData: { program_id, excel_file, mapping (JSON string) }
   * Response: { job_id, status, message }
   */
  VALIDATE_ACHIEVEMENT_IMPORT: `${API_BASE_URL}loyalty-target/validate-achievement-import`,

  /**
   * Get achievement gap analysis (validation results)
   * GET loyalty-target/get-achievement-gap-analysis?job_id=...&program_id=1
   */
  GET_ACHIEVEMENT_GAP_ANALYSIS: `${API_BASE_URL}loyalty-target/get-achievement-gap-analysis`,

  /**
   * Import achievement async (submit validated data for import)
   * POST loyalty-target/achievement-import-async?program_id=1
   * Body: { job_id, program_id }
   */
  ACHIEVEMENT_IMPORT_ASYNC: `${API_BASE_URL}loyalty-target/achievement-import-async`,

  /**
   * Upload achievements via Excel file (async processing) - OLD
   * POST loyalty-target/acievement-import-async?program_id=1
   * Content-Type: multipart/form-data
   * FormData: { target_id, excel_file, no_target, config_template_id }
   */
  IMPORT_UPLOAD_ASYNC_OLD: `${API_BASE_URL}loyalty-target/achievement-import-async`,

  /**
   * Upload achievements via Excel file (immediate or batch) - OLD
   * POST loyalty-target/acievement-import?program_id=1
   * Content-Type: multipart/form-data
   * FormData: { target_id, excel_file, no_target }
   */
  IMPORT_UPLOAD: `${API_BASE_URL}loyalty-target/acievement-import`,

  /**
   * Get batch process list
   * GET loyalty-target/get-achievement-import-batch
   */
  GET_BATCH_LIST: `${API_BASE_URL}loyalty-target/get-achievement-import-batch`,

  /**
   * Get point config templates (for no target uploads)
   * GET loyalty-target/get-point-config-template?program_id=1
   */
  GET_POINT_CONFIG_TEMPLATE: `${API_BASE_URL}loyalty-target/get-point-config-template`,

  /**
   * Upload achievements batch process
   * POST loyalty-target/achievement-import-batch
   * Content-Type: multipart/form-data
   */
  UPLOAD_BATCH: `${API_BASE_URL}loyalty-target/achievement-import-batch`,
} as const;

/**
 * Maker-Checker Report Endpoints
 */
export const MAKER_CHECKER_ENDPOINTS = {
  /**
   * Generate maker-checker report (ASYNC)
   * POST loyalty-target/maker-checker-async?program_id=1
   * Body: { report_name, target_id, type, program_id, no_target_type_id? }
   * Response: { job_id, status, message, check_status_url }
   */
  GENERATE_MAKER_CHECKER_ASYNC: `${API_BASE_URL}loyalty-target/maker-checker-async`,

  /**
   * Get generated reports list with pagination
   * GET loyalty-target/list-maker-check?program_id=1
   * Returns: X-Pagination-Total-Count, X-Pagination-Per-Page headers
   */
  LIST_MAKER_CHECK: `${API_BASE_URL}loyalty-target/list-maker-check`,

  /**
   * Upload approved Excel points (ASYNC)
   * POST loyalty-target/upload-points-async?program_id=1
   * Content-Type: multipart/form-data
   * FormData: { target_id, excel_file }
   */
  UPLOAD_APPROVED_ASYNC: `${API_BASE_URL}loyalty-target/upload-points-async`,

  /**
   * Reject points/report
   * POST loyalty-target/reject-points
   * Body: { id, target_id, approve_status: 3 }
   */
  REJECT_POINTS: `${API_BASE_URL}loyalty-target/reject-points`,

  /**
   * Get upload points validation config (field mappings for upload points)
   * GET loyalty-target/get-upload-points-validation-config?program_id=1&target_id=23
   * Response: { total_fields, fields: [{ field_name, field_code }], target_info }
   */
  GET_UPLOAD_POINTS_VALIDATION_CONFIG: `${API_BASE_URL}loyalty-target/get-upload-points-validation-config`,

  /**
   * Validate upload points
   * POST loyalty-target/validate-upload-points?program_id=1
   * Content-Type: multipart/form-data
   * FormData: { program_id, target_id, excel_file, mapping (JSON string) }
   * Response: { job_id, target_id, status, message, target_info }
   */
  VALIDATE_UPLOAD_POINTS: `${API_BASE_URL}loyalty-target/validate-upload-points`,

  /**
   * Trigger virtual / manual maker-checker
   * POST loyalty-target/virtual-maker-checker?program_id=1
   * No request body
   */
  VIRTUAL_MAKER_CHECKER: `${API_BASE_URL}loyalty-target/virtual-maker-checker`,
} as const;

/**
 * All Target Achievements Endpoints
 */
export const TARGET_ACHIEVEMENTS_ENDPOINTS = {
  ...POINT_STRUCTURE_ENDPOINTS,
  ...TARGET_ENDPOINTS,
  ...TARGET_QUARTER_ENDPOINTS,
  ...TARGET_CYCLE_ENDPOINTS,
  ...BRANDS_ENDPOINTS,
  ...ACHIEVEMENTS_ENDPOINTS,
  ...MAKER_CHECKER_ENDPOINTS,
} as const;
